import * as React from "react";
import Icon from "@/components/icons";
import { WaysToTakeCourseTableProps } from "./types";
import InfoTooltip from "@/components/ui/infoTooltip";
import { formatPrice } from "@/utils/functions/formatting";
import useTranslations from "@/i18n/useTranslations";

const CHECK = "x";
const UNLIMITED = "__unlimited__";
const LIMITED = "__limited__";
const FREE = "__free__";
const PRICE = "__price__";

export default function WaysToTakeCourseTable({
  coursePrice,
  convertedPrice,
  displayCurrency,
  children,
  rowHeight = "60px",
  hidePrice,
}: WaysToTakeCourseTableProps) {
  const t = useTranslations("Course");

  const comparisonSections = [
    {
      element: t("courseMaterials"),
      col1: UNLIMITED,
      col2: LIMITED,
      kind: "row" as const,
    },
    {
      element: t("edxSupport"),
      col1: CHECK,
      col2: CHECK,
      kind: "row" as const,
    },
    {
      element: t("careerNetwork"),
      col1: CHECK,
      col2: CHECK,
      kind: "row" as const,
    },
    {
      element: t("shareableCertificate"),
      col1: CHECK,
      col2: "",
      kind: "row" as const,
    },
    {
      element: t("gradedAssignmentsLabel"),
      col1: CHECK,
      col2: "",
      kind: "row" as const,
    },
    {
      element: t("price"),
      col1: "",
      col2: FREE,
      kind: PRICE,
    },
  ];

  if (!hidePrice) {
    const priceToDisplay =
      convertedPrice && displayCurrency
        ? formatPrice(convertedPrice, displayCurrency)
        : `$${coursePrice}`;
    comparisonSections[5].col1 = priceToDisplay;
  }

  const lastIndex = hidePrice
    ? comparisonSections.length - 2
    : comparisonSections.length - 1;

  const displayValue = (value: string) => {
    if (value === UNLIMITED) return t("unlimited");
    if (value === LIMITED) return t("limited");
    if (value === FREE) return t("free");
    return value;
  };

  return (
    // TABLE
    <div className="flex-1 pt-2 md:pt-0">
      <div className="grid grid-cols-[2fr_1fr_1fr] divide-y divide-primary-300">
        {/* HEADER ROW */}
        <div
          className="col-start-2 justify-items-center md:px-1 bg-brand-500 border border-brand-500"
          style={{ borderTopLeftRadius: "10px" }}
        >
          <div
            className="w-full max-w-[220px] text-center content-center"
            style={{ height: rowHeight }}
          >
            <div className="px-2 text-white text-xs md:text-base font-bold flex flex-row justify-center">
              {t("certificate")}
              <InfoTooltip
                text={t("certificateTooltip")}
                label={t("moreInformationAbout", {
                  option: t("certificate"),
                })}
                triggerClassName="ml-2 relative"
                iconSvgProps={{ width: "18", height: "18" }}
              />
            </div>
          </div>
        </div>
        <div
          className="bg-primary-700 justify-items-center md:px-8 border border-primary-700"
          style={{ borderTopRightRadius: "10px" }}
        >
          <div
            className="w-full max-w-[220px] text-center content-center"
            style={{ height: rowHeight }}
          >
            <div className="px-2 text-white text-xs md:text-base font-bold flex flex-row justify-center">
              {t("audit")}
              <InfoTooltip
                text={t("auditTooltip")}
                label={t("moreInformationAbout", { option: t("audit") })}
                triggerClassName="ml-2 relative"
                iconSvgProps={{ width: "18", height: "18" }}
              />
            </div>
          </div>
        </div>

        {/* DATA COMPARISON ROWS */}
        {comparisonSections.map((section, index) => {
          if (section.kind === PRICE && hidePrice) {
            return null;
          }
          let showCol2 = true;
          let showCol1 = true;
          return (
            <React.Fragment key={index}>
              {/* ELEMENT */}
              <div
                className={`content-center bg-putty-200`}
                style={{
                  borderTopLeftRadius: index === 0 ? "10px" : "",
                  borderBottomLeftRadius: index === lastIndex ? "10px" : "",
                  height: rowHeight,
                }}
              >
                <div className="text-xs md:text-sm font-bold ml-3 md:ml-6 text-primary-500">
                  {section.element}
                </div>
              </div>

              {/* COL1 */}
              <div
                className={`content-center inline-grid text-center text-white justify-items-center ${index === lastIndex && !hidePrice ? "bg-brand-500 text-base font-bold" : "bg-brand-700 text-sm"}`}
              >
                {section.col1 === CHECK &&
                  ((showCol1 = false),
                  (
                    <div className="rounded-full">
                      <Icon
                        type="circle-check-fill"
                        svgProps={{ width: "22", height: "22" }}
                        pathProps={{ fill: "white" }}
                      />
                    </div>
                  ))}
                {section.col1 === UNLIMITED &&
                  ((showCol1 = false),
                  (
                    <div className="content-center inline-grid text-center text-sm bg-brand-700">
                      {displayValue(section.col1)}
                    </div>
                  ))}
                {showCol1 && (
                  <div className="content-center inline-grid text-center text-base font-bold bg-brand-500">
                    {displayValue(section.col1)}
                  </div>
                )}
              </div>

              {/* COL2 */}
              <div
                className={`content-center inline-grid text-center text-sm text-white justify-items-center ${index === lastIndex && !hidePrice ? "bg-primary-500" : "bg-primary-700"} `}
                style={{
                  borderBottomRightRadius: index === lastIndex ? "10px" : "",
                }}
              >
                {section.col2 === CHECK &&
                  ((showCol2 = false),
                  (
                    <div className="rounded-full">
                      <Icon
                        type="circle-check-fill"
                        svgProps={{ width: "22", height: "22" }}
                        pathProps={{ fill: "white" }}
                      />
                    </div>
                  ))}
                {section.col2 === "" ||
                  (section.col2 === LIMITED &&
                    ((showCol2 = false),
                    (
                      <div className="content-center inline-grid text-center text-lsm bg-primary-700">
                        {displayValue(section.col2)}
                      </div>
                    )))}
                {showCol2 && (
                  <div className="content-center inline-grid text-center text-base font-bold bg-primary-500">
                    {displayValue(section.col2)}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* BUTTONS */}
      {children && (
        <div className="pt-8 md:justify-between gap-4">{children}</div>
      )}
    </div>
  );
}
